import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Building2, Plus, Pencil, Trash2, RefreshCw, Search, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const BLANK = {
  name: "",
  city: "",
  state: "",
  state_code: "",
  zip: "",
  address: "",
  phone: "",
  website: "",
  hours: "",
  services: [],
  lat: null,
  lng: null,
  notes: "",
  is_partner: false,
};

export default function AdminSheltersList({ token }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // shelter doc or null
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shelters", { headers, params: q ? { q } : {} });
      setItems(data.items || []);
      setServices(data.services || []);
    } catch {
      toast.error("Failed to load shelters.");
    } finally {
      setLoading(false);
    }
  }, [token, headers, q]);

  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (!window.confirm(`Delete shelter "${s.name}"?`)) return;
    setBusyId(s.id);
    try {
      await api.delete(`/admin/shelters/${s.id}`, { headers });
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="border border-zinc-900 bg-white" data-testid="admin-shelters">
      <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-3 flex-wrap justify-between">
        <div className="flex items-center gap-2">
          <Building2 size={14} className="text-red-600" />
          <span className="label-eyebrow">Local Shelters & Animal Resources</span>
          <span className="text-xs text-zinc-500 font-mono">({items.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name / city / zip"
              className="border border-zinc-300 pl-6 pr-2 py-1 text-xs w-56"
              data-testid="admin-shelters-search"
            />
          </div>
          <button type="button" onClick={load} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="admin-shelters-refresh">
            <RefreshCw size={12}/> Refresh
          </button>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 border border-red-700 hover:bg-red-700"
            data-testid="admin-shelters-add"
          >
            <Plus size={12}/> Add Shelter
          </button>
        </div>
      </div>

      <div className="divide-y divide-zinc-200 max-h-[720px] overflow-y-auto">
        {loading && (
          <div className="p-6 text-sm text-zinc-500 inline-flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-6 text-sm text-zinc-500">No shelters found.</div>
        )}
        {!loading && items.map((s) => (
          <div key={s.id} className="p-4 flex flex-col md:flex-row md:items-start gap-3" data-testid={`admin-shelter-row-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2">
                <h4 className="font-display font-black text-base leading-tight">{s.name}</h4>
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-zinc-300 bg-zinc-50 text-zinc-700">
                  {s.city ? `${s.city}, ` : ""}{s.state_code || s.state}
                </span>
                {s.is_partner && (
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border border-red-700 bg-red-50 text-red-700">
                    Partner
                  </span>
                )}
              </div>
              {s.address && <div className="text-xs text-zinc-600 mt-1">{s.address}</div>}
              <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                {s.phone && <span>{s.phone}</span>}
                {s.website && (
                  <a href={s.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-red-700 hover:underline">
                    <ExternalLink size={11}/> {s.website.replace(/^https?:\/\//, "").split("/")[0]}
                  </a>
                )}
                {s.hours && <span className="text-zinc-500">{s.hours}</span>}
              </div>
              {s.services?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.services.map((svc) => (
                    <span key={svc} className="text-[10px] font-mono px-1.5 py-0.5 border border-zinc-300 bg-zinc-50">{svc}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex md:flex-col gap-2 md:min-w-[130px]">
              <button
                type="button"
                onClick={() => setEditing(s)}
                className="inline-flex items-center justify-center gap-1 btn-outline text-xs"
                data-testid={`admin-shelter-edit-${s.id}`}
              >
                <Pencil size={12}/> Edit
              </button>
              <button
                type="button"
                disabled={busyId === s.id}
                onClick={() => del(s)}
                className="inline-flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-2 text-red-700 border border-red-700 hover:bg-red-50 disabled:opacity-60"
                data-testid={`admin-shelter-delete-${s.id}`}
              >
                <Trash2 size={12}/> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {(editing || creating) && (
        <ShelterEditModal
          shelter={editing || BLANK}
          services={services}
          headers={headers}
          isNew={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function ShelterEditModal({ shelter, services, headers, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK, ...shelter, services: shelter.services || [] });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? null : Number(e.target.value) }));

  const toggleService = (svc) => setForm((f) => ({
    ...f,
    services: f.services.includes(svc) ? f.services.filter((x) => x !== svc) : [...f.services, svc],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.state?.trim()) {
      toast.error("Name and State are required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/shelters", form, { headers });
        toast.success("Shelter created.");
      } else {
        await api.put(`/admin/shelters/${shelter.id}`, form, { headers });
        toast.success("Shelter updated.");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-shelter-edit-modal">
      <div className="bg-white border-2 border-zinc-900 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600">
          <div>
            <div className="label-eyebrow text-red-500">{isNew ? "Add" : "Edit"} Shelter</div>
            <div className="font-display font-black text-xl mt-1">{form.name || "New shelter"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white hover:text-red-500 text-xl" aria-label="Close">×</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Name *</label><input className="field" value={form.name} onChange={set("name")} required data-testid="admin-shelter-edit-name"/></div>
            <div><label className="label-eyebrow block mb-1">City</label><input className="field" value={form.city} onChange={set("city")}/></div>
            <div><label className="label-eyebrow block mb-1">State *</label><input className="field" value={form.state} onChange={set("state")} required/></div>
            <div><label className="label-eyebrow block mb-1">State code</label><input className="field" value={form.state_code} onChange={set("state_code")} maxLength={2}/></div>
            <div><label className="label-eyebrow block mb-1">ZIP</label><input className="field" value={form.zip} onChange={set("zip")}/></div>
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Address</label><input className="field" value={form.address} onChange={set("address")}/></div>
            <div><label className="label-eyebrow block mb-1">Phone</label><input className="field" value={form.phone} onChange={set("phone")}/></div>
            <div><label className="label-eyebrow block mb-1">Website</label><input className="field" value={form.website} onChange={set("website")}/></div>
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Hours</label><input className="field" value={form.hours} onChange={set("hours")}/></div>
            <div><label className="label-eyebrow block mb-1">Latitude</label><input type="number" step="any" className="field" value={form.lat ?? ""} onChange={setNum("lat")}/></div>
            <div><label className="label-eyebrow block mb-1">Longitude</label><input type="number" step="any" className="field" value={form.lng ?? ""} onChange={setNum("lng")}/></div>
            <div className="sm:col-span-2"><label className="label-eyebrow block mb-1">Notes</label><textarea rows={3} className="field" value={form.notes} onChange={set("notes")}/></div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow block mb-1">Services</label>
              <div className="flex flex-wrap gap-1">
                {(services || []).map((svc) => {
                  const active = form.services.includes(svc);
                  return (
                    <button
                      key={svc}
                      type="button"
                      onClick={() => toggleService(svc)}
                      className={`text-[11px] px-2 py-1 border ${
                        active
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"
                      }`}
                    >
                      {svc}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="btn-outline text-xs">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-xs disabled:opacity-60" data-testid="admin-shelter-edit-save">
              {saving ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} {isNew ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
